import { SearchIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, TextInput, View } from 'react-native';

import type { RouteCoordinate } from '@sixam/shared/route-planner-core';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { cn } from '@sixam/ui/utils';

import { authedFetch } from '@/lib/authed-fetch';
import type { RoutePlacePrediction } from '@/lib/route-place-search-provider';

type RoutePlaceSearchProps = Readonly<{
  focus: RouteCoordinate;
  onSelect: (place: RoutePlacePrediction) => void;
  tone?: 'dark' | 'default';
}>;

export function RoutePlaceSearch({ focus, onSelect, tone = 'default' }: RoutePlaceSearchProps) {
  const [query, setQuery] = React.useState('');
  const [predictions, setPredictions] = React.useState<RoutePlacePrediction[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 3) return;
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({
          lat: String(focus.latitude),
          lng: String(focus.longitude),
          q: trimmed,
        });
        const response = await authedFetch(`/api/routes/places?${params.toString()}`, {
          signal: controller.signal,
        });
        const body = (await response.json()) as {
          error?: string;
          predictions?: RoutePlacePrediction[];
        };
        if (!response.ok) throw new Error(body.error ?? 'Location search failed.');
        setPredictions(body.predictions ?? []);
        setError(null);
      } catch (searchError) {
        if (!(searchError instanceof Error && searchError.name === 'AbortError')) {
          setError(searchError instanceof Error ? searchError.message : 'Location search failed.');
          setPredictions([]);
        }
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [focus.latitude, focus.longitude, query]);

  function selectPlace(place: RoutePlacePrediction) {
    onSelect(place);
    setQuery(place.name);
    setPredictions([]);
  }

  return (
    <View className="gap-2">
      <View
        className={cn(
          'min-h-12 flex-row items-center gap-3 border-b px-1',
          tone === 'dark' ? 'border-white/20' : 'border-border'
        )}
      >
        <Icon
          as={SearchIcon}
          className={cn('size-5', tone === 'dark' ? 'text-white/70' : 'text-muted-foreground')}
        />
        <TextInput
          accessibilityLabel="Search anywhere"
          autoCapitalize="none"
          autoCorrect={false}
          className={cn(
            'min-h-12 flex-1 font-body text-base outline-none',
            tone === 'dark' ? 'text-white' : 'text-foreground'
          )}
          onChangeText={(value) => {
            setQuery(value);
            if (value.trim().length < 3) setPredictions([]);
          }}
          placeholder="Search any city or place"
          placeholderTextColor={tone === 'dark' ? 'rgba(255,255,255,0.5)' : '#71717A'}
          value={query}
        />
        {isSearching ? <ActivityIndicator /> : null}
      </View>
      {error ? <Text className="text-sm text-destructive">{error}</Text> : null}
      <View>
        {predictions.map((prediction) => (
          <Pressable
            accessibilityRole="button"
            className={cn(
              'min-h-14 justify-center border-b py-2 active:opacity-60',
              tone === 'dark' ? 'border-white/20' : 'border-border'
            )}
            key={prediction.id}
            onPress={() => selectPlace(prediction)}
          >
            <Text className={tone === 'dark' ? 'text-white' : undefined}>{prediction.name}</Text>
            {prediction.label !== prediction.name ? (
              <Text
                variant="muted"
                className={tone === 'dark' ? 'text-white/70' : undefined}
                numberOfLines={2}
              >
                {prediction.label}
              </Text>
            ) : null}
          </Pressable>
        ))}
      </View>
    </View>
  );
}
